import { useEffect } from 'react';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import MainNav from 'src/components/MainNav';
import PageHeader from 'src/components/PageHeader';
import TeamMemberAvatar from 'src/components/TeamMemberAvatar';
import TeamMemberInviteButton from 'src/components/TeamMemberInviteButton';
import { TeamMemberStatuses, TeamRoles } from 'src/constants/TeamRoles';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import useEnvironment from 'src/hooks/useEnvironment';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { getTeamMemberDisplayName } from 'src/utils/nameUtils';

export const Route = createFileRoute('/(app)/care/team/')({
    component: TeamComponent,
});

const PageName = 'Care Team Page';

function TeamComponent() {
    const navigate = useNavigate();

    const { trackPageView, trackEvent } = useAnalytics();
    const { isMobile } = useResponsive();
    const { communityCaregivers, licensedCaregivers, currentUser } = useTeamMemberData();
    const { isProd } = useEnvironment();

    const canInviteTeamMembers =
        currentUser?.isTeamAdmin ||
        currentUser?.role === TeamRoles.CareRecipient ||
        currentUser?.role === TeamRoles.TeamAdmin;

    const handleTeamMemberClick = (teamMemberRid: string) => {
        trackEvent('Team Member Clicked', { context: PageName, teamMemberRid });
        navigate({
            to: '/care/team/member/$teamMemberRid',
            params: { teamMemberRid },
        });
    };

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InnerMaxWidthContainer>
            <Stack sx={{ gap: 4 }}>
                <PageHeader pageTitle="Team" />

                <Stack sx={{ gap: 4 }} divider={<Divider />}>
                    {!isProd && (
                        <Stack sx={{ gap: 2.5, containerType: 'inline-size' }}>
                            <Typography
                                component="h3"
                                variant="body1"
                                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                            >
                                Only visible in dev
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                onClick={() => {
                                    navigate({ to: '/dev/reset-onboarding' });
                                }}
                            >
                                Onboarding Settings
                            </Button>
                        </Stack>
                    )}

                    {/* Licensed Caregivers */}
                    <Stack sx={{ gap: 2.5, containerType: 'inline-size' }}>
                        <Typography
                            component="h3"
                            variant="body1"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                        >
                            Professional Caregivers
                        </Typography>

                        <Box
                            sx={{
                                width: 'fit-content',
                                display: 'grid',
                                gridTemplateColumns: {
                                    '@': 'repeat(3, auto)',
                                    '@400': 'repeat(4, auto)',
                                    '@800': 'repeat(5, auto)',
                                },
                                gap: 4,
                            }}
                        >
                            {licensedCaregivers.map((caregiver) => (
                                <Stack
                                    key={caregiver.teamMemberRid}
                                    sx={{ gap: 2, alignItems: 'center' }}
                                >
                                    <TeamMemberAvatar
                                        teamMember={caregiver}
                                        isLicensedCaregiver
                                        onClick={() =>
                                            handleTeamMemberClick(caregiver.teamMemberRid)
                                        }
                                        size="large"
                                    />
                                    <Stack sx={{ alignItems: 'center', textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {getTeamMemberDisplayName(caregiver, {
                                                initializeLast: true,
                                            })}
                                        </Typography>
                                        {caregiver.teamMemberStatus ===
                                            TeamMemberStatuses.Invited && (
                                            <Typography variant="subtitle2">
                                                {isMobile ? 'Pending' : 'Pending Invitation'}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            ))}

                            {canInviteTeamMembers && (
                                <Stack sx={{ gap: 2, alignItems: 'center', textAlign: 'center' }}>
                                    <TeamMemberInviteButton
                                        size="large"
                                        onClick={() => {
                                            trackEvent('Invite Licensed Caregiver Button Clicked', {
                                                context: 'Team Page',
                                            });
                                            navigate({
                                                to: '/care/team/invite',
                                                search: { role: TeamRoles.LicensedCaregiver },
                                            });
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Invite
                                    </Typography>
                                </Stack>
                            )}
                        </Box>
                    </Stack>

                    {/* Community Caregivers */}
                    <Stack sx={{ gap: 2.5, containerType: 'inline-size' }}>
                        <Typography
                            component="h3"
                            variant="body1"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                        >
                            Family Members & Friends
                        </Typography>

                        <Box
                            sx={{
                                width: 'fit-content',
                                display: 'grid',
                                gridTemplateColumns: {
                                    '@': 'repeat(3, auto)',
                                    '@400': 'repeat(4, auto)',
                                    '@800': 'repeat(5, auto)',
                                },
                                gap: 4,
                            }}
                        >
                            {communityCaregivers.map((caregiver) => (
                                <Stack
                                    key={caregiver.teamMemberRid}
                                    sx={{ gap: 2, alignItems: 'center' }}
                                >
                                    <TeamMemberAvatar
                                        teamMember={caregiver}
                                        isAdmin={caregiver.isTeamAdmin}
                                        onClick={() =>
                                            handleTeamMemberClick(caregiver.teamMemberRid)
                                        }
                                        size="large"
                                    />
                                    <Stack sx={{ alignItems: 'center', textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {getTeamMemberDisplayName(caregiver, {
                                                initializeLast: true,
                                            })}
                                        </Typography>
                                        {caregiver.teamMemberStatus ===
                                            TeamMemberStatuses.Invited && (
                                            <Typography variant="subtitle2">
                                                {isMobile ? 'Pending' : 'Pending Invitation'}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            ))}

                            {canInviteTeamMembers && (
                                <Stack sx={{ gap: 2, alignItems: 'center', textAlign: 'center' }}>
                                    <TeamMemberInviteButton
                                        size="large"
                                        onClick={() => {
                                            trackEvent(
                                                'Invite Community Caregiver Button Clicked',
                                                { context: 'Team Page' }
                                            );
                                            navigate({
                                                to: '/care/team/invite',
                                                search: { role: TeamRoles.CommunityCaregiver },
                                            });
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Invite
                                    </Typography>
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </Stack>

            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    );
}
